import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { Stack, TextField, Dialog, DialogTitle, DialogContent, MenuItem, DialogActions, Button } from '@mui/material';
import { FormProvider, RHFSelect } from '../../../components/hook-form';

import { fDateYMD } from '../../../utils/formatTime';
import { getAuditBranches } from '../../branches/store/branchActions';
import { useHasRole } from '../../../shared/auth/useProfile';
import { ROLES } from '../../../shared/auth/roles';

// The shape of the filter, in one place: the page owns the values so they
// survive paging and a refresh, and hands them back here as the form's
// starting point.
export const EMPTY_TRANSFER_FILTERS = {
  locationId: '',
  transferStatus: '',
  custodianStatus: '',
  startDate: '',
  endDate: '',
  // Not a field in this dialog — it is the table's search box — but part of
  // the filter set, so it is carried through rather than wiped when this
  // dialog is applied.
  searchTerm: '',
};

const STATUSES = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Accepted', value: 'ACCEPTED' },
  { label: 'Rejected', value: 'REJECTED' },
];

TransferFilter.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  values: PropTypes.object,
  onApply: PropTypes.func.isRequired,
};

export default function TransferFilter({ open, handleClose, values = EMPTY_TRANSFER_FILTERS, onApply }) {
  // A manager only ever sees transfers they are a party to, so narrowing by
  // branch would be a dropdown with one entry.
  const isManager = useHasRole(ROLES.MANAGER);
  const dispatch = useDispatch();
  const { auditBranches } = useSelector((state) => state.branchReducer);

  const [selectedStartDate, setSelectedStartDate] = useState(values.startDate || null);
  const [selectedEndDate, setSelectedEndDate] = useState(values.endDate || null);

  const filterSchema = Yup.object().shape({});
  // Reopening the dialog shows what is currently applied, rather than blanks
  // over a filtered table.
  const defaultValues = { ...EMPTY_TRANSFER_FILTERS, ...values };

  const methods = useForm({ resolver: yupResolver(filterSchema), defaultValues });
  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleStartDateChange = (newValue) => {
    setValue('startDate', fDateYMD(newValue));
    setSelectedStartDate(newValue);
  };
  const handleEndDateChange = (newValue) => {
    setValue('endDate', fDateYMD(newValue));
    setSelectedEndDate(newValue);
  };

  // The page fetches; this dialog only says what to fetch.
  const onSubmit = async (data) => {
    onApply({ ...EMPTY_TRANSFER_FILTERS, ...values, ...data });
    handleClose();
  };

  const handleClear = () => {
    reset(EMPTY_TRANSFER_FILTERS);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    // Clears this dialog's fields. The search box has its own Clear.
    onApply({ ...EMPTY_TRANSFER_FILTERS, searchTerm: values.searchTerm || '' });
    handleClose();
  };

  useEffect(() => {
    dispatch(getAuditBranches({}));
  }, [dispatch]);

  return (
    <Dialog
      maxWidth="md"
      open={open}
      fullWidth
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>Filter Transfers</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            {!isManager && (
              <RHFSelect
                fullWidth
                name="locationId"
                label="Branch"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
              >
                {auditBranches?.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </RHFSelect>
            )}
            <RHFSelect
              fullWidth
              name="transferStatus"
              label="Admin Decision"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              {STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFSelect
              fullWidth
              name="custodianStatus"
              label="Custodian Decision"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              {STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
            <DatePicker
              label="Start Date"
              value={selectedStartDate}
              format="yyyy-MM-dd"
              onChange={handleStartDateChange}
              renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker
              label="End Date"
              value={selectedEndDate}
              format="yyyy-MM-dd"
              onChange={handleEndDateChange}
              renderInput={(params) => <TextField {...params} />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" variant="contained" color="error" onClick={handleClose}>
            Cancel
          </Button>
          <Button size="small" variant="outlined" onClick={handleClear}>
            Clear
          </Button>
          <LoadingButton type="submit" size="small" variant="contained" loading={isSubmitting}>
            Filter
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
